import Backbone from 'backbone'
import template from './members.hbs'
import $ from 'jquery'
import _ from 'lodash'
import conductor from 'scoreboard/components/conductor'
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'
import MultiPrincipalChooser from 'scoreboard/components/multi-principal-chooser'
import memberItem from './member-item.hbs'
import i18n from 'scoreboard/components/i18n'
import groupType from './groups/group-type'
import guard from 'scoreboard/components/unsaved/guard'

// cache of potential members for the current organization.  keep it outside
// individual MembersView instances.  Note this is cleared out in the
// remove method of the view so navigating to a new screen always clears the
// cache
var potentialMembers

var MembersView = Backbone.View.extend({

	/**
	 * @returns {String} the URL used to drill down on a member
	 */
	drilldownUrl: (principal) => '/admin/' + principal.type + 's/' + principal.id,

	className: 'member-list-panel',

	events: {
		'click .delete-membership': 'delete',
		'click li.member-item': 'drilldown'
	},

	/**
	 * @param {Principal[]} [options.members]
	 * 	The members to render (e.g. the local admins of a group)
	 * 	This should be a list of principal objects with {id, type, groupType, label}
	 * 	@see Principal.java
	 *
	 * @param options.addForm
	 * 	A form to display in a tooltip when the user the Add button
	 *
	 * @param options.icon
	 * 	The icon to display next to each member
	 *
	 * @param [options.title=Members]
	 * 	The title for the display
	 *
	 * @param [options.noneMessage=There are no members]
	 * 	A message when there are no members
	 *
	 * @param [options.editable=false]
	 * 	Indicates whether the members of the group can be edited
	 *
	 * @param [options.url='api/principals']
	 * 	Used to retrieve the list of available members
	 *
	 * @param {Principal} [options.exclude] A principal that should be excluded form the list
	 * e.g. The group that the members list is for
	 */
	initialize: function(options = {}) {
		this.options = options
		const {exclude} = this.options
		this.url = this.options.url || 'api/principals'
		this.excludedPrincipal = exclude && {id: exclude.id, type: exclude.domainType}
	},

	render: function() {
		this.adding = false

		this.$el.empty().append(template(this.context()))
		this.renderMemberItems()
		this.renderPrincipalChooser()

		this.covertButtonTooltips = new CovertButtonTooltips({ el: this.el })

		return this
	},

	renderMemberItems: function() {
		if (!this.options.editable && _.isEmpty(this.options.members)) {
			this.$('.list').addClass('is-not-displayed')
			this.$('.none-message').removeClass('is-not-displayed')
			return
		}

		var sorted = _.sortBy(this.options.members, function(member) {
			return (member.label || '').toLocaleUpperCase()
		})
		_.each(sorted, function(member) {
			this.$('.list').append(memberItem(this.getMemberItemContext(member)))
		}.bind(this))
	},

	renderPrincipalChooser: function() {
		if (!this.options.editable) {
			this.$('.list-add').toggleClass('is-not-displayed', true)
		}
		this.principalChooser = new MultiPrincipalChooser({
			el: this.$('.list-add'),
			hideSelectedOptions: true, // So selected items aren't rendered in the dropdown OR in the select control. See #14299
			url: this.url,
			cache: potentialMembers,
			currentPrincipals: this.options.members,
			exclude: this.excludedPrincipal
		}).render()
		this.principalChooser.on('load', function(data) {
			potentialMembers = data
		})
		this.principalChooser.on('select', this.add.bind(this))
	},

	context: function() {
		return _.extend({
			title: 'admin.group.groupMembers.short',
			noneMessage: i18n.get('common.thereAreNo', 'admin.group.groupMembers.short')
		}, this.options, {
			addMessage: this.addMessage()
		})
	},

	// Render the given icon for each member
	/* TODO: after incorporate once profile pictures are implemented
	renderMembers: function(icon) {
		_.each(this.options.members, function(member) {
			this.members.$el.find('[data-member-id=' + member.id + '] .member-icon')
				.append(icon)
		}.bind(this))
	},
	*/

	drilldown: function(e) {
		// ignore clicks on the delete membership button
		if ($(e.target).hasClass('delete-membership')) {
			return
		}
		guard.conditionallyRun(() => {
			// the data should have id and type properties which is what we need
			var data = $(e.currentTarget).data()
			conductor.navigate(this.drilldownUrl(data), { trigger: true })
		})
	},

	addMessage: function() {
		return i18n.get('admin.group.groupMembers.add')
	},

	add: function(member) {
		this.$('.list .list-add').after(memberItem(this.getMemberItemContext(member)))
		this.updateMemberCount(1)
		this.trigger('change', this.getMembers())
	},

	'delete': function(e) {
		var li = $(e.target).closest('.member-item')
		var data = li.data()
		// use duck typing and send along all the .data, which should include
		// id and type properties
		this.principalChooser.removePrincipal(li.data())
		li.remove()
		this.trigger('change', this.getMembers())
		this.updateMemberCount(-1)
	},

	updateMemberCount: function(amount) {
		var count = this.$('.member-count').text()
		count = amount + parseInt(count)
		this.$('.member-count').text(count)
	},

	/**
	 * Returns the data attributes for each member
	 * (IDs and types of the selected members)
	 */
	getMembers: function() {
		return _.map(this.$('li[data-id]'), function(el) {
			return $(el).data()
		})
	},

	/**
	 * @returns {JQuery} A jquery object for each member item in the list.
	 * Allows caller to add additional data attributes (e.g. dataset tree principals)
	 */
	getMemberEls: function() {
		return this.$('.member-item')
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.covertButtonTooltips, this.principalChooser], 'remove')
		potentialMembers = null
	},

	getMemberItemContext: function(member) {
		return _.extend({
			icon : _.isFunction(this.options.icon) ? this.options.icon(member) : this.options.icon,
			groupTypeLabel: member.domainType ? groupType.label(member.type) : groupType.label(member.groupType)
		}, member)
	}

})

export default MembersView

MembersView.__filename__ = 'admin/principals/members.js'
